<template>
  <div class="ship-layer-container">
    <div ref="shipLayerContainer" class="ship-d3-layer"></div>
  </div>
</template>

<script>
import * as d3 from 'd3'
import L from 'leaflet'

export default {
  name: 'ShipCanvasLayer',
  props: {
    ships: {
      type: Array,
      required: true
    },
    map: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      svg: null,
      shipGroup: null,
      shipElements: {},
      bounds: null,
      layerInitialized: false,
      initRetryCount: 0,
      _lastUpdateTime: null
    }
  },
  watch: {
    ships: {
      handler() {
        this.updateShips();
      },
      deep: true
    },
    map: {
      handler(newMap) {
        if (newMap && !this.layerInitialized) {
          // 延迟一下初始化，确保DOM完全渲染
          setTimeout(() => {
            this.initD3Layer();
          }, 200);
        }
      },
      immediate: true
    }
  },
  mounted() {
    console.log('ShipCanvasLayer mounted');
    // 使用nextTick确保DOM已经渲染
    this.$nextTick(() => {
      console.log('ShipCanvasLayer nextTick');
      if (this.map) {
        // 延迟初始化，确保DOM渲染完成
        setTimeout(() => {
          this.initD3Layer();
        }, 200);
      }
    });
  },
  beforeDestroy() {
    this.removeEventListeners();
    if (this.svg) {
      this.svg.remove();
    }
  },
  methods: {
    initD3Layer() {
      if (!this.map || this.layerInitialized) return;
      
      console.log('初始化D3图层', ++this.initRetryCount);
      
      try {
        // 确保容器元素存在
        if (!this.$refs.shipLayerContainer) {
          console.error('D3容器元素引用不存在', this.initRetryCount);
          // 延迟重试
          if (this.initRetryCount < 5) {
            setTimeout(() => {
              this.initD3Layer();
            }, 300);
          }
          return;
        }
        
        // 创建SVG容器
        const container = d3.select(this.$refs.shipLayerContainer);
        if (!container.node()) {
          console.error('无法找到D3容器元素');
          return;
        }
        
        console.log('D3容器初始化成功');
        const size = this.map.getSize();
        console.log('地图尺寸:', size);
        
        this.svg = container.append('svg')
          .attr('width', size.x)
          .attr('height', size.y)
          .style('position', 'absolute')
          .style('top', 0)
          .style('left', 0)
          .style('pointer-events', 'none') // 允许事件穿透到地图
          .style('z-index', 499); // 确保在地图控件下方，但在地图瓦片上方
        
        // 创建船舶组
        this.shipGroup = this.svg.append('g');
        
        // 绑定事件
        this.map.on('move', this.onMapMove);
        this.map.on('zoom', this.onMapZoom);
        this.map.on('zoomanim', this.onMapZoomAnim); // 添加缩放动画事件
        this.map.on('moveend', this.onMapMove);
        this.map.on('zoomend', this.onMapZoomEnd);
        this.map.on('resize', this.onMapResize);
        
        // 只有船舶元素接收鼠标事件，其他区域允许事件穿透
        // 不要在整个容器上设置pointer-events
        
        this.layerInitialized = true;
        console.log('D3图层初始化完成');
        
        // 首次更新船舶
        this.updateShips();
      } catch (err) {
        console.error('D3图层初始化错误:', err);
      }
    },
    
    updateShips() {
      if (!this.map || !this.svg || !this.shipGroup || !this.ships) return;
      
      // 减少日志频率，防止缩放过程中刷屏
      if (this._lastUpdateTime && Date.now() - this._lastUpdateTime < 20) {
        // 过于频繁的更新会影响性能
        return;
      }
      this._lastUpdateTime = Date.now();
      
      const self = this;
      
      try {
        // 转换船舶数据
        const shipData = this.ships
          .filter(ship => ship && ship.pos)
          .map(ship => {
            try {
              const latlng = L.latLng(ship.pos.latitude, ship.pos.longitude);
              const point = this.map.latLngToContainerPoint(latlng);
              return {
                id: ship.userid,
                x: point.x,
                y: point.y,
                heading: ship.cog !== undefined ? ship.cog / 10 : 0,
                ship: ship
              };
            } catch (e) {
              console.error('船舶数据转换错误:', e, ship);
              return null;
            }
          })
          .filter(Boolean); // 过滤掉转换失败的船舶
        
        // 数据绑定
        const ships = this.shipGroup.selectAll('.ship')
          .data(shipData, d => d.id);
        
        // 移除不再存在的船舶
        ships.exit().remove();
        
        // 创建新船舶
        const enter = ships.enter().append('g')
          .attr('class', 'ship')
          .attr('transform', d => `translate(${d.x}, ${d.y})`)
          .style('cursor', 'pointer')
          .style('pointer-events', 'all')
          .style('transition', 'transform 0.1s');
        
        // 修复D3 v7的事件处理方式
        enter.on('click', (event, d) => {
          if (event) {
            event.stopPropagation();
          }
          this.$emit('ship-click', d.ship);
        });
        
        // 创建船舶三角形图标
        enter.append('path')
          .attr('d', this.createShipPath(12)) // 12是大小
          .attr('fill', '#3a5eea')
          .attr('stroke', '#1c2f7b')
          .attr('stroke-width', 1.5)
          .attr('transform', d => `rotate(${d.heading})`)
          .style('transition', 'transform 0.2s');
        
        // 使用D3的过渡效果实现平滑移动
        // 更新现有船舶位置和方向
        ships
          .attr('transform', d => `translate(${d.x}, ${d.y})`)
          .select('path')
          .attr('transform', d => `rotate(${d.heading})`);
      } catch (err) {
        console.error('更新船舶图层错误:', err);
      }
    },
    
    createShipPath(size) {
      // 创建锐角三角形SVG路径
      return `M0,${-size} L${size * 0.6},${size * 0.8} L0,${size * 0.3} L${-size * 0.6},${size * 0.8} Z`;
    },
    
    // 地图事件处理
    onMapMove() {
      this.updateShips();
    },
    
    onMapZoom() {
      this.updateShips();
    },
    
    // 处理地图缩放动画
    onMapZoomAnim(e) {
      // 如果浏览器支持CSS变换，可以通过调整SVG的transform来跟随地图缩放
      if (!this.svg || !e.zoom) return;

      try {
        // 获取当前缩放级别和目标缩放级别
        const currentZoom = this.map.getZoom();
        const targetZoom = e.zoom;
        const scale = this.map.getZoomScale(e.zoom, currentZoom);
        
        // 获取缩放中心点在当前视图上的像素坐标
        const centerPoint = this.map.getSize().divideBy(2);
        const viewHalf = centerPoint;
        const origin = centerPoint.multiplyBy(scale).subtract(viewHalf).round();
        
        // 应用适当的CSS变换来跟随地图动画
        this.svg
          .style('transform-origin', '0 0')
          .style('transform', `translate(${-origin.x}px, ${-origin.y}px) scale(${scale})`);
          
      } catch (err) {
        console.error('处理缩放动画错误:', err);
      }
    },
    
    // 缩放结束时恢复正常绘制
    onMapZoomEnd() {
      if (this.svg) {
        // 重置变换
        this.svg.style('transform', null);
        this.svg.style('transform-origin', null);
      }
      // 重新绘制船舶
      this.updateShips();
    },
    
    onMapResize() {
      if (!this.map || !this.svg) return;
      
      const size = this.map.getSize();
      this.svg
        .attr('width', size.x)
        .attr('height', size.y);
      
      this.updateShips();
    },
    
    removeEventListeners() {
      if (this.map) {
        this.map.off('move', this.onMapMove);
        this.map.off('zoom', this.onMapZoom);
        this.map.off('zoomanim', this.onMapZoomAnim);
        this.map.off('moveend', this.onMapMove);
        this.map.off('zoomend', this.onMapZoomEnd);
        this.map.off('resize', this.onMapResize);
      }
    }
  }
}
</script>

<style scoped>
.ship-layer-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none; /* 整个容器不接收鼠标事件，允许事件穿透 */
  z-index: 499; /* 在地图控件下方，但在瓦片上方 */
  overflow: visible;
}

.ship-d3-layer {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none; /* 允许事件穿透到地图 */
}

/* 确保船舶图标可见 - Vue 2深度选择器 */
.ship-layer-container >>> .ship {
  pointer-events: all; /* 只有船舶元素接收鼠标事件 */
  cursor: pointer;
}

.ship-layer-container >>> .ship path {
  fill-opacity: 0.9;
  stroke-opacity: 0.9;
  transform-origin: center center;
  will-change: transform;
}

/* SVG容器的过渡属性 */
.ship-layer-container >>> svg {
  will-change: transform;
  transform-origin: 0 0;
}
</style> 